import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompleteM365Callback } from "@/lib/m365";

/**
 * Microsoft redirects here after admin consent + auth-code grant.
 * URL shape: /auth/callback/m365_full?code=...&state=...
 *
 * The page exchanges the code for tokens via relay-m365-auth-callback,
 * then bounces to /integrations/m365 on success. Errors stay here so the
 * user sees what went wrong (most commonly: admin consent not granted).
 */
export default function AuthCallbackM365() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteM365Callback();
  const [error, setError] = useState<string | null>(null);
  const [consentRequired, setConsentRequired] = useState(false);
  // The OAuth code is single-use. Guard against React StrictMode's double
  // effect-invocation (and any remount) so we never exchange the code twice —
  // a second exchange fails with invalid_grant and would overwrite a
  // successful connect's UI with a spurious error.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const oauthError = searchParams.get("error");
    const oauthErrorDescription = searchParams.get("error_description");

    // Microsoft can redirect with ?error= when the user cancels or admin
    // consent is missing. Surface that without invoking the callback.
    if (oauthError) {
      const description = oauthErrorDescription ?? oauthError;
      setError(description);
      // AADSTS65001 in the description = consent_required
      if (
        description.includes("AADSTS65001") ||
        oauthError === "consent_required"
      ) {
        setConsentRequired(true);
      }
      return;
    }

    if (!code || !state) {
      setError("Missing OAuth code or state in the callback URL.");
      return;
    }

    complete.mutate(
      { code, state },
      {
        onSuccess: (data) => {
          if (data.consent_required) {
            setConsentRequired(true);
            setError(
              data.error ?? "Admin consent has not been granted for the new permissions.",
            );
            return;
          }
          navigate("/integrations/m365", { replace: true });
        },
        onError: (e) => {
          const message = e instanceof Error ? e.message : "Connection failed";
          setError(message);
          if (message.includes("AADSTS65001") || message.includes("consent")) {
            setConsentRequired(true);
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-10">
      {error ? (
        <div className="max-w-md space-y-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertCircle className="mr-2 inline h-4 w-4" />
            {error}
          </div>
          {consentRequired && (
            <div className="rounded-md border bg-muted/30 p-4 text-sm">
              <p className="font-medium">Admin consent required</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Your Microsoft 365 tenant admin needs to grant consent for
                Relay's expanded permissions (Mail.Read, Contacts.ReadWrite,
                Files.ReadWrite, MailboxSettings.Read) in the Entra admin
                center → API permissions tab. Once granted, return here and
                try again.
              </p>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/integrations/m365", { replace: true })}
          >
            Back to Microsoft 365 settings
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Completing Microsoft 365 connection…
        </div>
      )}
    </div>
  );
}
